/** One photo window, as a frame's `drawParams.images` stores it. */
export interface PhotoWindow {
  type: string
  left: number
  top: number
  width: number
  height: number
}

/** The parts of a frame's `drawParams` the layout reads. */
export interface StripGeometry {
  canvasWidth: number
  canvasHeight: number
  images: PhotoWindow[]
}

/** Where one copy of a set lands, in the units a sticker placement uses. */
export interface SetStamp {
  /** Centre, as a percentage of strip width / height. */
  xPct: number
  yPct: number
  /** Width as a percentage of strip width; height follows the art. */
  widthPct: number
  rotation: number
}

/** A set's width, as a share of the photo window it sits on, before `setScale`. */
export const SET_WIDTH = 0.4

/**
 * How much bigger or smaller a set is drawn, from how much of its image is ink.
 *
 * One width for every set made a spray of four small stars — mostly empty
 * space — read as specks next to a sewing button that fills its whole square.
 * Scaling by the square root of the empty share evens out how much each set
 * weighs on the strip, within bounds so neither extreme runs away.
 *
 * @param coverage share of the image's pixels that are not transparent (0–1).
 */
export const setScale = (coverage: number): number =>
  Math.min(1.3, Math.max(0.85, Math.sqrt(0.45 / Math.max(coverage, 0.01))))

/** How far each copy may turn from its anchor's base angle, either way. */
const JITTER_DEG = 9

/** A gutter taller than this share of a window is artwork, not a gap. */
const BAND = 0.12

/** Kept clear of the strip's outer edge, as a share of the canvas. */
const EDGE = 0.012

/**
 * A frame's photo windows grouped into columns, left to right, each read top
 * to bottom. A 2x6 strip is one column; a 4x6 sheet of two strips is two, and
 * each of those is cut apart into its own printed strip.
 *
 * Columns are CLUSTERED, not compared for equality: hand-drawn borders wander
 * a few pixels per window, and sorting on `left` alone interleaved the rows.
 * Group by a gap wide enough to separate two real columns and far wider than
 * the drift, then read each group top to bottom.
 */
export function photoColumnsOf<T extends PhotoWindow>(
  geometry: { canvasWidth: number; images: T[] } | undefined
): T[][] {
  const slots = (geometry?.images ?? []).filter((image) => image.type.startsWith('photo-'))
  if (slots.length === 0) return []
  const columnGap = (geometry?.canvasWidth ?? 600) * 0.2

  const byLeft = [...slots].sort((a, b) => a.left - b.left)
  const columns: T[][] = [[byLeft[0]]]
  for (const slot of byLeft.slice(1)) {
    const current = columns[columns.length - 1]
    const previous = current[current.length - 1]
    if (slot.left - previous.left > columnGap) columns.push([slot])
    else current.push(slot)
  }
  return columns.map((column) => [...column].sort((a, b) => a.top - b.top))
}

/**
 * Lay a sticker set out on a strip, the same way on every frame.
 *
 * A set is ONE image — a designer's own cluster (a spray of stars, a bow with
 * flowers) — placed whole: the guest picks the set, never the pieces. Where it
 * goes is worked out from the frame's photo windows, so a frame added next
 * month is decorated without anybody laying it out. Per column:
 *
 * - the top-left corner of the first photograph,
 * - the outer edge of the gutter between the middle two photographs,
 * - the bottom-right corner of the last photograph, turned half a circle so the
 *   cluster reads as the first one's mirror.
 *
 * A column of one photograph gets its two corners. Every anchor sits on a
 * corner or a gutter — never the middle of a window, where the faces are. Each
 * column is kept inside its own share of the canvas: a two-strip sheet is cut
 * down the middle, and a set that crossed the cut would print in two halves.
 * When a column does not fit its equal share (one wide photo beside a stack
 * of small ones), the shares split at the gutters between columns instead.
 *
 * Deterministic in `seed`, so the preview the guest approves is the print.
 *
 * @param artRatio the set image's height / width.
 * @param scale from `setScale`, so sparse and solid sets weigh the same.
 */
export function layoutSet(
  geometry: StripGeometry | undefined,
  artRatio: number,
  seed: string,
  scale = 1
): SetStamp[] {
  const columns = photoColumnsOf(geometry)
  if (!geometry || columns.length === 0) return []

  const W = geometry.canvasWidth
  const H = geometry.canvasHeight
  const random = rng(seed)
  const jitter = (): number => (random() * 2 - 1) * JITTER_DEG
  const stamps: SetStamp[] = []

  const bounds = columnBounds(columns, W)
  columns.forEach((column, index) => {
    const { left, right } = bounds[index]
    const first = column[0]
    const last = column[column.length - 1]

    const anchors: { x: number; y: number; window: PhotoWindow; base: number }[] = [
      {
        x: first.left + 0.12 * first.width,
        y: first.top + 0.1 * first.height,
        window: first,
        base: 0
      }
    ]
    if (column.length > 1) {
      const above = column[Math.floor(column.length / 2) - 1]
      const below = column[Math.floor(column.length / 2)]
      const gap = below.top - (above.top + above.height)
      anchors.push({
        x: above.left + 0.9 * above.width,
        // A gutter far taller than a hairline is a designed band — a partner's
        // logo row, say — so the set moves up onto the photograph's edge
        // instead of covering it.
        y:
          gap > BAND * above.height
            ? above.top + 0.8 * above.height
            : (above.top + above.height + below.top) / 2,
        window: above,
        base: 0
      })
    }
    anchors.push({
      x: last.left + 0.86 * last.width,
      y: last.top + 0.9 * last.height,
      window: last,
      base: 180
    })

    for (const anchor of anchors) {
      const width = SET_WIDTH * scale * anchor.window.width
      const height = width * artRatio
      const x = clamp(anchor.x, left + EDGE * W + width / 2, right - EDGE * W - width / 2)
      const y = clamp(anchor.y, EDGE * H + height / 2, H - EDGE * H - height / 2)
      stamps.push({
        xPct: (x / W) * 100,
        yPct: (y / H) * 100,
        widthPct: (width / W) * 100,
        rotation: anchor.base + jitter()
      })
    }
  })

  return stamps
}

/** Each column's horizontal share of the canvas; see layoutSet. */
export function columnBounds(columns: PhotoWindow[][], W: number): { left: number; right: number }[] {
  const n = columns.length
  const extent = columns.map((column) => ({
    left: Math.min(...column.map((w) => w.left)),
    right: Math.max(...column.map((w) => w.left + w.width))
  }))
  const slack = 0.005 * W
  const fitsEqualShares = extent.every(
    (e, i) => e.left >= (W / n) * i - slack && e.right <= (W / n) * (i + 1) + slack
  )
  if (fitsEqualShares) {
    return extent.map((_, i) => ({ left: (W / n) * i, right: (W / n) * (i + 1) }))
  }
  return extent.map((_, i) => ({
    left: i === 0 ? 0 : (extent[i - 1].right + extent[i].left) / 2,
    right: i === n - 1 ? W : (extent[i].right + extent[i + 1].left) / 2
  }))
}

export const clamp = (v: number, lo: number, hi: number): number =>
  lo > hi ? (lo + hi) / 2 : Math.min(hi, Math.max(lo, v))

/** Small seeded PRNG (FNV-1a hash into mulberry32). */
export function rng(seed: string): () => number {
  let h = 2166136261
  for (let i = 0; i < seed.length; i++) h = Math.imul(h ^ seed.charCodeAt(i), 16777619)
  let a = h >>> 0
  return () => {
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}
